import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Spinner,
  useDisclosure,
  useToast,
  Tooltip,
  Badge,
  Input,
  Textarea,
  ButtonGroup,
} from "@chakra-ui/react";
import ReactPlayer from "react-player";
import { useParams, useHistory } from "react-router-dom";
import { ChevronRightIcon } from "@chakra-ui/icons";
import DeleteSceneModal from "../Modals/DeleteSceneModal";
import {
  useGenerateSceneMutation,
  useGetSceneResultQuery,
  useGetVideoPreviewsQuery,
} from "services/api";
import { API_URL } from "constants";

// Function to format milliseconds as HH:MM:SS.mm
const formatDuration = (milliseconds) => {
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const ms = Math.floor((milliseconds % 1000) / 10);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
};

// EditScene component for editing scene details
const EditScene = ({ scene, videoUrl, onCancel, onGenerate, onSave }) => {
  const [title, setTitle] = useState(scene.title || "");
  const [prompt, setPrompt] = useState(scene.prompt || "");

  const handleSaveAsDraft = () => {
    onSave({ ...scene, title, prompt, status: "draft" });
  };

  const handleGenerate = () => {
    onGenerate({ ...scene, title, prompt });
  };

  return (
    <Box borderRadius="lg" borderWidth="1px" p={4}>
      <Heading as="h4" mb={2} size="sm">
        Title
      </Heading>
      <Input
        mb={4}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Scene title"
        value={title}
      />

      <Heading as="h4" mb={2} size="sm">
        Prompt
      </Heading>
      <Textarea
        mb={4}
        minHeight="150px"
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Enter your scene prompt"
        value={prompt}
      />

      <Flex justifyContent="flex-end">
        <ButtonGroup spacing={3}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            colorScheme="blue"
            onClick={handleSaveAsDraft}
            variant="outline"
          >
            Save as Draft
          </Button>
          <Button colorScheme="blue" onClick={handleGenerate}>
            {videoUrl ? "Regenerate" : "Generate"}
          </Button>
        </ButtonGroup>
      </Flex>
    </Box>
  );
};

export default function SceneDetails() {
  const { projectId, sceneId } = useParams();
  const history = useHistory();
  const { isOpen, onOpen: openDeleteModal, onClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const toast = useToast();
  const [generateScene] = useGenerateSceneMutation();

  // Fetch the complete scene data using the API
  const { data: scene, isLoading, error } = useGetSceneResultQuery(
    { token: projectId, sceneName: sceneId },
    { skip: !sceneId }
  );
  const prompt = scene?.scene_data?.description || "No prompt available";

  // Fetch video previews to get the video URL
  const { data: previewsData } = useGetVideoPreviewsQuery(projectId, {
    skip: !projectId,
  });

  // Helper function to get video URL from previews
  const getVideoUrl = () => {
    if (!previewsData?.previews || !scene?.scene_name) return null;

    const matchingPreview = previewsData.previews.find(
      (preview) => preview.scene_name === scene.scene_name
    );

    if (matchingPreview?.preview_url) {
      // Construct the full URL by combining API_URL with the preview_url
      return `${API_URL}${matchingPreview.preview_url}`;
    }

    return null;
  };

  const videoUrl = getVideoUrl();
  const isGenerating = scene?.status === "generating";

  const handleScenesClick = () => {
    history.push(`/v2/project/${projectId}`);
  };

  // Handle scene deletion
  const handleDeleteScene = async () => {
    try {
      setIsDeleting(true);

      // Get the scene title before deletion for the toast message
      const sceneTitle = scene?.title || scene?.scene_name || "Scene";

      // TODO: Implement real API call for scene deletion
      // For now, we'll just show a success message
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Navigate back to project view
      history.push(`/v2/project/${projectId}`);

      // Close modal
      onClose();

      // Show success toast
      toast({
        title: "Scene deleted",
        description: `"${sceneTitle}" has been successfully deleted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      console.error("Failed to delete scene:", error);

      // Show error toast
      toast({
        title: "Deletion failed",
        description: "There was an error deleting the scene. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle saving edited scene
  const handleSaveScene = async (updatedScene) => {
    try {
      // Use the API to update the scene
      await generateScene({
        token: projectId,
        sceneData: {
          ...updatedScene,
          status: "draft",
        },
      }).unwrap();

      setIsEditing(false);

      toast({
        title: "Scene updated",
        description: `"${updatedScene.title}" has been updated.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      console.error("Failed to update scene:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating the scene. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  // Handle generate/regenerate scene
  const handleGenerateScene = async (updatedScene) => {
    try {
      // Use the API to regenerate the scene
      await generateScene({
        token: projectId,
        sceneData: {
          ...updatedScene,
          status: "generating",
        },
      }).unwrap();

      setIsEditing(false);

      toast({
        title: "Scene generation started",
        description: `"${updatedScene.title}" is now being generated.`,
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      console.error("Failed to generate scene:", error);
      toast({
        title: "Generation failed",
        description:
          "There was an error generating the scene. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  };

  // Show loading state while fetching scene data
  if (isLoading) {
    return (
      <Flex
        alignItems="center"
        direction="column"
        height="50vh"
        justifyContent="center"
        width="100%"
      >
        <Spinner color="blue.500" mb={4} size="xl" thickness="4px" />
        <Text color="gray.600">Loading scene details...</Text>
      </Flex>
    );
  }

  // Show error state if scene fetch failed
  if (error) {
    return (
      <Box borderRadius="lg" borderWidth="1px" p={4}>
        <Heading as="h3" mb={2} size="md">
          Error Loading Scene
        </Heading>
        <Text color="red.500" mb={4}>
          {error?.data?.message ||
            "Failed to load scene details. Please try again."}
        </Text>
        <Button colorScheme="blue" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteSceneModal
        handleDeleteScene={handleDeleteScene}
        isDeleting={isDeleting}
        isOpen={isOpen}
        onClose={onClose}
        sceneId={sceneId}
        sceneTitle={scene?.title || "Scene"}
      />

      <Flex alignItems="center" mb={6} width="100%">
        <Breadcrumb
          separator={<ChevronRightIcon color="gray.500" />}
          spacing="8px"
          width="100%"
        >
          <BreadcrumbItem>
            <BreadcrumbLink onClick={handleScenesClick}>Scenes</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem
            flexShrink={1}
            isCurrentPage
            maxWidth="calc(100% - 100px)"
          >
            <Tooltip label={scene?.title || "Scene not found"}>
              <Text
                as="span"
                display="block"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                width="100%"
              >
                {scene?.title || "Scene not found"}
              </Text>
            </Tooltip>
          </BreadcrumbItem>
        </Breadcrumb>
      </Flex>

      {sceneId ? (
        (() => {
          if (!scene) {
            return <Text>Scene not found.</Text>;
          }

          if (isEditing) {
            return (
              <EditScene
                onCancel={() => setIsEditing(false)}
                onGenerate={handleGenerateScene}
                onSave={handleSaveScene}
                scene={scene}
                videoUrl={videoUrl}
              />
            );
          }

          if (isGenerating) {
            return (
              <Box borderRadius="lg" borderWidth="1px" p={4}>
                <Heading as="h3" mb={2} size="md">
                  {scene.title}
                </Heading>
                <Text color="gray.500" fontSize="sm" mb={2}>
                  ID: {scene.scene_name}
                </Text>

                <Flex
                  alignItems="center"
                  bg="gray.50"
                  borderRadius="md"
                  direction="column"
                  height="300px"
                  justifyContent="center"
                  mb={4}
                  width="100%"
                >
                  <Spinner color="blue.500" mb={4} size="xl" thickness="4px" />
                  <Heading as="h4" mb={2} size="sm">
                    Generating Scene
                  </Heading>
                  <Text color="gray.600" maxWidth="80%" textAlign="center">
                    This scene is currently being generated. You&apos;ll be
                    notified when it&apos;s ready to view.
                  </Text>
                </Flex>

                <Heading as="h4" mb={2} size="sm">
                  Prompt
                </Heading>
                <Text mb={4}>{prompt}</Text>
              </Box>
            );
          }

          return (
            <Box borderRadius="lg" borderWidth="1px" p={4}>
              <Flex alignItems="center" mb={2}>
                <Heading as="h3" size="md">
                  {scene.title || scene.scene_name}
                </Heading>
                {scene.status === "draft" && (
                  <Badge colorScheme="purple" ml={2}>
                    Draft
                  </Badge>
                )}
              </Flex>
              <Text color="gray.500" fontSize="sm" mb={2}>
                ID: {scene.scene_name}
              </Text>
              {!!scene.duration && (
                <Text color="gray.500" fontSize="sm" mb={4}>
                  Duration: {formatDuration(scene.duration)}
                </Text>
              )}
              {!!videoUrl && (
                <Box mb={4} width="100%">
                  <ReactPlayer
                    controls
                    height="300px"
                    url={videoUrl}
                    width="100%"
                  />
                </Box>
              )}
              <Heading as="h4" mb={2} size="sm">
                Prompt
              </Heading>
              <Text mb={4}>{prompt}</Text>

              <Flex justifyContent="flex-end">
                <ButtonGroup spacing={3}>
                  {/* <Button colorScheme="blue" onClick={() => setIsEditing(true)}>
                    Edit Scene
                  </Button> */}
                  <Button colorScheme="red" onClick={openDeleteModal}>
                    Delete Scene
                  </Button>
                </ButtonGroup>
              </Flex>
            </Box>
          );
        })()
      ) : (
        <Text>Select a scene from the right sidebar to view details.</Text>
      )}
    </>
  );
}
